import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { NOVA_URL, COMPLIANCE_FRAMEWORKS } from "../data/site";

const easeOut = [0.22, 1, 0.36, 1];

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden border-b border-zinc-200 bg-white pt-32 pb-20 lg:pt-36 lg:pb-24"
    >
      {/* subtle premium depth — soft radial glow + faint grid, almost invisible */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-50/50 via-white to-white pointer-events-none" aria-hidden="true" />
      <div className="absolute top-[-12%] left-1/2 -translate-x-1/2 h-[560px] w-[880px] rounded-full bg-blue-500/[0.07] blur-[150px] pointer-events-none" aria-hidden="true" />
      <div className="absolute inset-0 grid-overlay opacity-40 pointer-events-none" aria-hidden="true" />

      <div className="relative mx-auto max-w-3xl px-6 lg:px-8 flex flex-col items-center text-center">
        <motion.a
          href={NOVA_URL}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: easeOut }}
          className="group inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/80 backdrop-blur px-3.5 py-1.5 text-xs shadow-sm hover:border-zinc-300 hover:shadow transition-all"
          data-testid="hero-badge"
        >
          <span className="text-zinc-600">We&apos;re the company behind</span>
          <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white">
            Nova
          </span>
          <ArrowRight className="w-3.5 h-3.5 text-zinc-400 transition-transform group-hover:translate-x-0.5" />
        </motion.a>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: easeOut, delay: 0.08 }}
          className="mt-7 font-display text-4xl sm:text-5xl lg:text-[3.25rem] font-semibold tracking-tight text-zinc-900 leading-[1.04]"
          data-testid="hero-headline"
        >
          Building AI Products That{" "}
          <span className="text-gradient-blue">Solve Real Business Problems</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: easeOut, delay: 0.16 }}
          className="mt-7 max-w-2xl text-base text-zinc-500 leading-relaxed"
          data-testid="hero-subheading"
        >
          Our flagship product, <span className="text-zinc-900 font-medium">Nova</span>, helps
          organisations simplify compliance and certification through intelligent automation.
          We also offer selected AI consulting and IT services for teams embracing digital
          transformation.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: easeOut, delay: 0.24 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href={NOVA_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-explore-nova-btn"
            className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 text-[15px] font-medium text-white bg-blue-600 rounded-full hover:bg-blue-500 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-lg shadow-blue-600/25"
          >
            Explore Nova
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#contact"
            data-testid="hero-talk-to-us-btn"
            className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 text-[15px] font-medium text-zinc-900 border border-zinc-300 bg-white rounded-full hover:bg-zinc-50 hover:border-zinc-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Talk to Us
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 flex flex-col items-center"
          data-testid="hero-frameworks"
        >
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-zinc-400">
            Nova helps you achieve
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2.5">
            {COMPLIANCE_FRAMEWORKS.map((f) => (
              <span
                key={f}
                className="rounded-full border border-zinc-200 bg-zinc-100/80 px-3.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-white hover:border-zinc-300 transition-colors"
              >
                {f}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
